import React, { useState, useRef } from 'react';
import { DndContext, useDraggable, DragOverlay } from '@dnd-kit/core';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus } from 'react-icons/fi';

// ── Panel card (draggable from the list) ─────────────────────────────────────
const PanelCard = ({ card }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: card.id,
    data: { type: 'panel', label: card.content },
  });

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`px-3 py-2.5 rounded-md border text-xs font-bold cursor-grab active:cursor-grabbing select-none transition-all touch-none
        ${isDragging
          ? 'opacity-30 bg-gray-50 border-gray-100 text-gray-300'
          : 'bg-white border-gray-200 text-gray-600 hover:border-rutabaga-pink/40 hover:shadow-sm'
        }`}
    >
      {card.content}
    </div>
  );
};

// ── Placed card on canvas (draggable to reposition) ───────────────────────────
const PlacedCard = ({ placement, onRemove, readonly }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: placement.id,
    data: { type: 'placed', label: placement.label },
    disabled: readonly,
  });

  // No centering transform on the draggable element itself — dnd-kit uses
  // offset* properties (not getBoundingClientRect) to position the DragOverlay,
  // which don't include CSS transforms, causing a jump.
  // We compensate by subtracting half the chip dimensions in handleDragEnd instead.
  const style = {
    position: 'absolute',
    left: `${placement.x_value * 100}%`,
    top: `${placement.y_value * 100}%`,
    opacity: isDragging ? 0 : 1,
    zIndex: 10,
    touchAction: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1 pl-2.5 pr-1.5 py-1.5 rounded-full text-xs font-bold shadow-md border-2 border-white select-none whitespace-nowrap
        ${isDragging ? 'bg-rutabaga-pink-hover text-white scale-105' : 'bg-rutabaga-pink text-white'}
        ${!readonly ? 'cursor-grab active:cursor-grabbing' : 'cursor-default'}`}
      {...(readonly ? {} : { ...listeners, ...attributes })}
    >
      <span className="max-w-[140px] truncate">{placement.label}</span>
      {!readonly && (
        <button
          className="ml-0.5 w-4 h-4 rounded-full hover:bg-white/25 flex items-center justify-center transition-colors shrink-0"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onRemove(placement.id); }}
        >
          ×
        </button>
      )}
    </div>
  );
};

// ── Floating overlay while dragging ──────────────────────────────────────────
const DragCard = ({ label }) => (
  <div className="px-2.5 py-1.5 rounded-full text-xs font-bold shadow-xl bg-rutabaga-pink text-white border-2 border-white cursor-grabbing whitespace-nowrap opacity-90">
    {label}
  </div>
);

// ── Main canvas ───────────────────────────────────────────────────────────────
const ExperienceMatrixCanvas = ({
  cards = [],          // touchpoint_cards rows
  placements = [],     // normalized: { id, x_value, y_value, label, is_custom, touchpoint_card_id }
  config = {},         // { x_left, x_right, y_top, y_bottom, allow_custom_cards, instructions }
  onPlaceCard,         // ({ cardId, x, y }) — place a pre-loaded card
  onMoveCard,          // ({ placementId, x, y }) — reposition a placed card
  onRemoveCard,        // (placementId) — remove a placement
  onAddCustomCard,     // ({ label, x, y }) — participant-created card
  readonly = false,
}) => {
  const [activeItem, setActiveItem] = useState(null); // { label } for DragOverlay
  const [customLabel, setCustomLabel] = useState('');

  // Direct ref to the canvas DOM element — used for reliable drop-position maths
  const canvasRef = useRef(null);

  // Cards not yet placed on the canvas
  const placedCardIds = new Set(
    placements.filter(p => !p.is_custom).map(p => p.touchpoint_card_id)
  );
  const unplacedCards = cards.filter(c => !placedCardIds.has(c.id));

  // ── Drag handlers ─────────────────────────────────────────────────────────
  const handleDragStart = ({ active }) => {
    setActiveItem({ label: active.data.current?.label || '' });
  };

  const handleDragEnd = ({ active }) => {
    setActiveItem(null);
    if (!active) return;

    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    // Use the dragged item's final translated rect (works with DragOverlay)
    const translated = active.rect.current.translated;
    if (!translated) return;

    const canvasRect = canvasEl.getBoundingClientRect();

    // Use top-left of the translated element (no centering transform on element,
    // so top-left matches where the user's grab point naturally lands)
    const cx = translated.left;
    const cy = translated.top;

    // Bail out if the centre isn't over the canvas at all
    if (cx < canvasRect.left || cx > canvasRect.right ||
        cy < canvasRect.top  || cy > canvasRect.bottom) return;

    // 0–1 fraction, kept 2% inside the edges so chips never land right on the border
    const x = Math.max(0.02, Math.min(0.98, (cx - canvasRect.left) / canvasRect.width));
    const y = Math.max(0.02, Math.min(0.98, (cy - canvasRect.top)  / canvasRect.height));

    if (active.data.current?.type === 'panel') {
      onPlaceCard?.({ cardId: active.id, x, y });
    } else if (active.data.current?.type === 'placed') {
      onMoveCard?.({ placementId: active.id, x, y });
    }
  };

  const handleAddCustom = () => {
    const label = customLabel.trim();
    if (!label) return;
    onAddCustomCard?.({ label, x: 0.5, y: 0.5 });
    setCustomLabel('');
  };

  const placedCount = placements.length;
  const totalCount = cards.length;

  return (
    <DndContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="flex h-full select-none overflow-hidden">

        {/* ── Left: card panel ─────────────────────────────────────────────── */}
        <div className="w-52 shrink-0 flex flex-col bg-white border-r border-gray-200">
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
              {readonly ? 'Cards' : 'Drag to place'}
            </p>
            {!readonly && totalCount > 0 && (
              <p className="text-[10px] text-gray-300 font-bold mt-0.5">
                {placedCount}/{totalCount} placed
              </p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {unplacedCards.length === 0 && !config.allow_custom_cards && (
              <p className="text-[10px] text-gray-300 font-bold text-center pt-6">
                {readonly ? 'All cards placed' : '🎉 All cards placed!'}
              </p>
            )}
            {unplacedCards.map(card =>
              readonly
                ? <div key={card.id} className="px-3 py-2 rounded-md border border-gray-100 text-xs font-bold text-gray-300">{card.content}</div>
                : <PanelCard key={card.id} card={card} />
            )}
          </div>

          {/* Custom card creator */}
          {!readonly && config.allow_custom_cards && (
            <div className="p-3 border-t border-gray-100">
              <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-2">
                Add custom card
              </p>
              <div className="flex gap-1.5">
                <input
                  type="text"
                  value={customLabel}
                  onChange={e => setCustomLabel(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddCustom()}
                  placeholder="Card label…"
                  className="flex-1 min-w-0 text-xs border border-gray-200 rounded px-2 py-1.5 outline-none focus:border-rutabaga-pink transition-colors"
                />
                <button
                  onClick={handleAddCustom}
                  disabled={!customLabel.trim()}
                  className="bg-rutabaga-pink text-white px-2 py-1.5 rounded hover:bg-rutabaga-pink-hover disabled:opacity-40 transition-colors shrink-0"
                >
                  <SafeIcon icon={FiPlus} className="text-xs" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Right: XY plot ───────────────────────────────────────────────── */}
        <div className="flex-1 flex items-center justify-center bg-rutabaga-bg overflow-hidden p-10">
          {/* Height-first sizing: canvas fills available height and derives its
              width from the 1:1 ratio. maxWidth:100% prevents horizontal overflow
              on narrow viewports, so the canvas always fits either dimension. */}
          <div className="relative" style={{ height: '100%', aspectRatio: '1 / 1', maxWidth: '100%' }}>

            {/* Axis labels */}
            <div className="absolute -top-7 inset-x-0 text-center">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                {config.y_top || 'Top'}
              </span>
            </div>
            <div className="absolute -bottom-7 inset-x-0 text-center">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">
                {config.y_bottom || 'Bottom'}
              </span>
            </div>
            <div className="absolute -left-14 inset-y-0 flex items-center justify-center">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest -rotate-90 whitespace-nowrap">
                {config.x_left || 'Left'}
              </span>
            </div>
            <div className="absolute -right-14 inset-y-0 flex items-center justify-center">
              <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest rotate-90 whitespace-nowrap">
                {config.x_right || 'Right'}
              </span>
            </div>

            {/* Drop target canvas */}
            <div
              ref={canvasRef}
              className="w-full h-full bg-white rounded-lg border border-gray-200 shadow-sm relative overflow-hidden"
            >
              {/* Axis lines */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-x-0 top-1/2 h-px bg-gray-100" />
                <div className="absolute inset-y-0 left-1/2 w-px bg-gray-100" />
              </div>

              {/* Empty state */}
              {placements.length === 0 && !readonly && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <p className="text-gray-200 font-black text-sm tracking-tight">
                    Drag cards onto the matrix
                  </p>
                </div>
              )}

              {/* Placed cards */}
              {placements.map(p => (
                <PlacedCard
                  key={p.id}
                  placement={p}
                  onRemove={onRemoveCard}
                  readonly={readonly}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Floating drag overlay */}
      <DragOverlay dropAnimation={null}>
        {activeItem && <DragCard label={activeItem.label} />}
      </DragOverlay>
    </DndContext>
  );
};

export default ExperienceMatrixCanvas;
